import { useState, useEffect } from 'react';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLocation } from 'react-router-dom';

const FloatingSocialButtons = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const dragControls = useDragControls();
  const location = useLocation();

  // Don't render on admin page
  if (location.pathname === '/mukesh') {
    return null;
  }

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check on initial load
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleButtons = () => {
    setIsOpen(!isOpen);
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: custom * 0.1,
        duration: 0.3,
        type: 'spring',
        stiffness: 260,
        damping: 20
      }
    })
  };

  const mainButtonVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.1 },
    pressed: { scale: 0.95 }
  };

  return (
    <div className="fixed bottom-20 left-4 z-40 flex flex-col items-center gap-3">
      <AnimatePresence>
        {isOpen && (
          <div className="flex flex-col gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.a
                    href="https://wa.me/9742846801"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-green-500 text-white shadow-lg hover:shadow-xl"
                    variants={buttonVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    custom={0}
                    whileHover={{ scale: 1.1, y: -5 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Chat on WhatsApp"
                  >
                    <MessageCircle size={20} />
                  </motion.a>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Chat on WhatsApp</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.a
                    href="mailto:business.dgit@gmail.com"
                    className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-500 text-white shadow-lg hover:shadow-xl"
                    variants={buttonVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    custom={1}
                    whileHover={{ 
                      scale: 1.1, 
                      y: -5,
                      rotate: [0, -10, 10, -10, 0],
                      transition: {
                        rotate: {
                          repeat: 0,
                          duration: 0.5
                        }
                      }
                    }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Send an email"
                  >
                    <Mail size={20} />
                  </motion.a>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Send us an email</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.a
                    href="https://www.facebook.com/dgitnepal"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-600 text-white shadow-lg hover:shadow-xl"
                    variants={buttonVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    custom={2}
                    whileHover={{ scale: 1.1, y: -5 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Visit our Facebook"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </motion.a>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Visit our Facebook</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <motion.a
                    href="tel:9742846801"
                    className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-purple-500 text-white shadow-lg hover:shadow-xl"
                    variants={buttonVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    custom={3}
                    whileHover={{ scale: 1.1, y: -5 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Call us now"
                  >
                    <Phone size={20} />
                  </motion.a>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Call us now</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </AnimatePresence>

      <motion.button
        drag
        dragControls={dragControls}
        dragMomentum={false}
        dragConstraints={{ top: -200, left: 0, right: 100, bottom: 200 }}
        onClick={toggleButtons}
        className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg hover:shadow-xl ${
          isOpen ? 'bg-red-500' : 'bg-gradient-to-r from-blue-500 to-purple-600'
        } text-white focus:outline-none`}
        variants={mainButtonVariants}
        initial="rest"
        whileHover="hover"
        whileTap="pressed"
        aria-label="Toggle social media buttons"
      >
        <motion.div
          animate={{
            rotate: isOpen ? 135 : 0
          }}
          transition={{ duration: 0.3 }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
        </motion.div>
      </motion.button>
    </div>
  );
};

export default FloatingSocialButtons;
